/**
 * `/join?token=…` — the only way an account comes into existence here.
 *
 * PUBLIC on purpose, and therefore deliberately NOT under `_authed`: the person
 * opening this link has no account, which is the whole point. `apps/api` refuses
 * self-serve sign-up outright, so a visitor without a working link has nothing
 * to do on this screen and is told so rather than bounced to a sign-up form that
 * does not exist.
 *
 * ## What this route owns, and what it does not
 *
 * The screen itself is `JoinPanel`: it reads the invitation, takes a password
 * and redeems. This route owns the two things that need the app's own
 * machinery — signing the new account in, and landing them in their CRM.
 *
 * ## The cached-null-session trap, again
 *
 * `_authed.beforeLoad` resolves the session with `ensureQueryData`, which serves
 * a CACHED value while it is fresh. Someone who wandered to a protected route
 * before opening their invitation has `session: null` sitting in that cache, and
 * navigating in a moment later would read the stale `null` and bounce them
 * straight back out — a brand-new account that appears not to work. So the
 * session entry is REMOVED, not invalidated: `ensureQueryData` hands back a
 * resolved-but-stale entry, and only removal forces the fetch. Same fix, same
 * reason, as `login.tsx`.
 */

import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { signInWithPassword } from '../api/auth.ts';
import { queryKeys } from '../api/keys.ts';
import { JoinPanel } from '../components/join/join-panel.tsx';
import { parseJoinSearch } from '../lib/search.ts';

export const Route = createFileRoute('/join')({
  component: JoinRoute,
  validateSearch: parseJoinSearch,
});

function JoinRoute() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <JoinPanel
      token={token}
      onAccountReady={async ({ email, password }) => {
        // The redeem route returns no session by design, so this is where the
        // account they just created becomes a signed-in one.
        await signInWithPassword({ email, password });
        queryClient.removeQueries({ queryKey: queryKeys.session() });
        await navigate({ to: '/' });
      }}
    />
  );
}
